import "../Login/Login.scss";

import React, { useEffect, useState } from "react";
import TextInputField from "../../Components/TextInput/TextInputField";
import { Divider, InputAdornment, SvgIcon, CircularProgress, Box } from "@mui/material";
import { Mail } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ButtonField from "../../Components/Button/ButtonField";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as Arrow } from "../../Assets/Images/Arrow.svg";
import { ReactComponent as eye } from "../../Assets/Images/eye.svg";
import { ReactComponent as ClosedEye } from "../../Assets/Images/closedEye.svg";

import { useFormik } from "formik";
import * as Yup from "yup";
import IconButtonField from "../../Components/Button/IconButtonField";
import { useDispatch } from "react-redux";
import {
  clearForgetPassword,
  forgetPassword,
} from "../../Redux/Slicers/ForgetPassword/ForgetPasswordSlicer";
import FailureResponseDialog from "../../Components/FailureResponseDialog/FailureResponseDialog";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Logo from '../../Assets/Images/navami_logo.webp';

interface ForgotProps {
  password: string;
  Cpassword: string;
}
const ForgotPassword = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { data: forgetPasswordResponse, isLoading } = useAppSelector(
    (state) => state.ForgetPassword
  );
  const [show, setShow] = useState<boolean>(false);
  const [showC, setShowC] = useState<boolean>(false);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<string>('');
  const [isVerifyingToken, setIsVerifyingToken] = useState<boolean>(true);
  
  const forgotInitialValues: ForgotProps = {
    password: '',
    Cpassword: '',
  };

  const forgotSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    Cpassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm Password is required'),
  });

  const nav = useNavigate();
  const token = searchParams.get('token');

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('Reset token is missing. Please use the link from your email.');
        setIsVerifyingToken(false);
        return;
      }

      try {
        const api = (await import('../../Redux/Axios/middleware')).default;
        const response = await api.get(`/parkingOwner/verify-reset-token?token=${token}`);
        
        if (response.data.responseCode === 200 && response.data.response?.status) {
          setTokenValid(true);
          setTokenError('');
        } else {
          setTokenError('Invalid or expired reset token. Please request a new password reset.');
        }
      } catch (error: any) {
        const errorMsg = error?.response?.data?.response?.error || 
                        error?.response?.data?.error ||
                        'Invalid or expired reset token. Please request a new password reset.';
        setTokenError(errorMsg);
        setTokenValid(false);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token]);

  const forgotForm = useFormik({
    initialValues: forgotInitialValues,
    validationSchema: forgotSchema,
    onSubmit: (value, { resetForm }) => {
      if (!token) {
        alert('Reset token is missing. Please use the link from your email.');
        return;
      }
      
      if (!tokenValid) {
        alert('Invalid reset token. Please request a new password reset.');
        return;
      }
      
      dispatch(
        forgetPassword({
          token: token,
          password: value.password,
          confirmPassword: value.Cpassword,
        })
      );
    },
  });

  useEffect(() => {
    // Redirect to login on successful password reset
    const isSuccess = forgetPasswordResponse?.responseMessage === 'success' || 
                      forgetPasswordResponse?.responseCode === 200 || 
                      (forgetPasswordResponse?.response?.status === true && forgetPasswordResponse?.responseCode !== undefined && forgetPasswordResponse?.responseCode >= 200 && forgetPasswordResponse?.responseCode < 300);
    
    if (isSuccess) {
      setTimeout(() => {
        nav('/login');
      }, 2000);
    }
  }, [forgetPasswordResponse, nav]);

  useEffect(() => {
    return () => {
      dispatch(clearForgetPassword());
    };
  }, [dispatch]);

  return (
    <div className="backGround">
      <form
        onSubmit={forgotForm.handleSubmit}
        className="main">
        <>
          <div className="header">
            {/* <SvgIcon component={Logo} inheritViewBox /> */}
            <img
              src={Logo}
              alt="Navami Logo"
            />

            <div className="heading" style={{ color: '#a8cc3d' }}>
              Reset Password
            </div>
            <div className="subHeading">
              {isVerifyingToken 
                ? 'Verifying reset link...' 
                : tokenValid 
                  ? 'Please enter your new password' 
                  : 'Invalid reset link'}
            </div>
            
            {tokenError && (
              <div style={{ 
                color: '#f44336', 
                textAlign: 'center', 
                marginTop: '10px',
                padding: '10px',
                backgroundColor: '#ffebee',
                borderRadius: '4px'
              }}>
                {tokenError}
                <br />
                <NavLink to="/forgot-password-mail" style={{ color: '#a8cc3d', textDecoration: 'underline' }}>
                  Request a new password reset
                </NavLink>
              </div>
            )}
          </div>

          {isVerifyingToken ? (
            <div className="textFields" style={{ textAlign: 'center', padding: '20px' }}>
              <p>Please wait while we verify your reset link...</p>
            </div>
          ) : tokenValid ? (
            <>
              <div className="textFields">
                <TextInputField
                  type={show ? 'text' : 'password'}
                  className="textField"
                  id="password"
                  name="password"
                  label="New Password"
                  value={forgotForm.values.password}
                  onChange={forgotForm.handleChange}
                  onBlur={forgotForm.handleBlur}
                  helperText={
                    forgotForm.touched.password && forgotForm.errors.password
                  }
                  error={
                    forgotForm.touched.password &&
                    Boolean(forgotForm.errors.password)
                  }
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <IconButtonField onClick={() => setShow(!show)} disabled={isLoading}>
                        <SvgIcon
                          sx={{
                            width: '20px',
                            height: '20px',
                          }}
                          component={show ? eye : ClosedEye}
                          inheritViewBox
                        />
                      </IconButtonField>
                    ),
                  }}
                />
                <TextInputField
                  type={showC ? 'text' : 'password'}
                  className="textField"
                  id="Cpassword"
                  name="Cpassword"
                  label="Confirm Password"
                  value={forgotForm.values.Cpassword}
                  onChange={forgotForm.handleChange}
                  onBlur={forgotForm.handleBlur}
                  helperText={
                    forgotForm.touched.Cpassword && forgotForm.errors.Cpassword
                  }
                  error={
                    forgotForm.touched.Cpassword &&
                    Boolean(forgotForm.errors.Cpassword)
                  }
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <IconButtonField onClick={() => setShowC(!showC)} disabled={isLoading}>
                        <SvgIcon
                          sx={{
                            width: '20px',
                            height: '20px',
                          }}
                          component={showC ? eye : ClosedEye}
                          inheritViewBox
                        />
                      </IconButtonField>
                    ),
                  }}
                />
              </div>
              <div className="textFields">
                <ButtonField
                  className="buttonfield"
                  size="large"
                  type="submit"
                  disabled={isLoading || !tokenValid}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <span>SUBMITTING...</span>
                    </Box>
                  ) : (
                    'Submit'
                  )}
                </ButtonField>
                <NavLink
                  className="login_page"
                  to={'/login'}
                  style={{ pointerEvents: isLoading ? 'none' : 'auto' }}>
                  <ButtonField
                    className="back_button"
                    size="large"
                    disabled={isLoading}>
                    <SvgIcon
                      component={Arrow}
                      inheritViewBox
                    />{' '}
                    Back
                  </ButtonField>
                </NavLink>
              </div>
            </>
          ) : null}
        </>
      </form>
      {(() => {
        const isSuccess = forgetPasswordResponse?.responseMessage === 'success' || 
                          forgetPasswordResponse?.responseCode === 200 || 
                          (forgetPasswordResponse?.response?.status === true && forgetPasswordResponse?.responseCode !== undefined && forgetPasswordResponse?.responseCode >= 200 && forgetPasswordResponse?.responseCode < 300);
        
        return isSuccess ? (
          <ResponseDialog 
            response={forgetPasswordResponse} 
          />
        ) : (
          <FailureResponseDialog response={forgetPasswordResponse} />
        );
      })()}
    </div>
  );
};

export default ForgotPassword;
